import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";

function formatValue(v, suffix) {
  if (suffix === "%") return `${Math.round(v * 10) / 10}%`;
  if (suffix === "x") return `${Math.round(v * 10) / 10}x`;
  return `${Math.round(v)}`;
}

export default function MetricsCounters() {
  const reduce = useReducedMotion();

  const metrics = useMemo(
    () => [
      { label: "Phishing resistance", value: 99.9, suffix: "%", hint: "with hardware keys" },
      { label: "Incident reduction", value: 70, suffix: "%", hint: "fewer identity-related incidents" },
      { label: "Onboarding speed", value: 80, suffix: "%", hint: "faster user onboarding" },
      { label: "Audit readiness", value: 100, suffix: "%", hint: "evidence-ready compliance" },
    ],
    [],
  );

  const [values, setValues] = useState(() => metrics.map(() => 0));

  useEffect(() => {
    if (reduce) return;

    const start = performance.now();
    const durationMs = 1100;

    let raf = 0;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setValues(metrics.map((m) => m.value * eased));
      if (p < 1) raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [metrics, reduce]);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {metrics.map((m, idx) => (
        <motion.div
          key={m.label}
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.45, delay: idx * 0.06 }}
          className="rounded-2xl border border-white/10 bg-[var(--gradient-card)] p-5"
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            {m.label}
          </div>
          <div
            className="mt-2 text-3xl font-semibold tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {formatValue(reduce ? m.value : (values[idx] ?? 0), m.suffix)}
          </div>
          <div className="mt-1 text-sm text-[var(--text-secondary)]">{m.hint}</div>
        </motion.div>
      ))}
    </div>
  );
}
